import publishingCoreService from 'commons/api/publishingCoreService/publishingCoreService';
import { PUBLISHING_SCOPE } from 'commons/publishing/publishingScope';
import assortmentSystemService from 'commons/api/assortmentSystemService/assortmentSystemService';
import localeService from 'commons/localeService/localeService';
import {
  APPAREL_LABEL_GROUP,
  iconForPublishingLabel,
} from '@spreadgroup/common-partner-lib/publishing';
import { getMainCategories } from '@spreadgroup/common-partner-lib/composition';
import { usePublishingStore } from '../stores/publishing';

// Shared by every awaiter so a login init and a publishing page mounting at the
// same time join one load instead of fetching everything twice.
let dataLoadPromise = null;

async function loadData({ dispatch, commit }) {
  commit('setLoadError', false);
  try {
    await Promise.all([
      dispatch('fetchConfig'),
      dispatch('fetchTranslations'),
      dispatch('fetchCategories'),
      dispatch('fetchAppearanceGroups'),
      dispatch('fetchAppearances'),
      dispatch('fetchShops'),
      dispatch('fetchApparelTargetGroupLabels'),
    ]);
    commit('setLoaded');
  } catch (error) {
    // Deliberately not rethrown: callers await this to find out *whether* the
    // data is there, and read `loadError` to render the degraded state.
    console.error('publishingCore/fetchData failed', error);
    commit('setLoadError', true);
  }
}

export default {
  namespaced: true,
  state: {
    config: null,
    translations: null,
    categories: null,
    mainCategories: null,
    apparelTargetGroupLabels: null,
    appearanceGroups: null,
    appearances: null,
    shops: null,
    loaded: false,
    loadError: false,
    intentsForScope: {
      [PUBLISHING_SCOPE.MP.key]: false,
      [PUBLISHING_SCOPE.SHOP.key]: false,
    },
  },
  getters: {
    registeredUserShops: (state, getters, rootState) =>
      rootState.user.shops.filter((userShop) =>
        state.shops.some((shop) => `${shop.shopId}` === userShop.id),
      ),
    hasRegisteredShops: (state) => state.shops && !!state.shops.length,
    hasMpIntent: (state) => state.intentsForScope[PUBLISHING_SCOPE.MP.key],
    hasShopIntent: (state) => state.intentsForScope[PUBLISHING_SCOPE.SHOP.key],
    hasIntentForCurrentScope: (state) => {
      const publishingStore = usePublishingStore();
      const scope = publishingStore.currentScope;

      return scope && state.intentsForScope[scope.key];
    },
    featureLabels: (state) =>
      state.translations?.labels?.GLOBAL_PUBLISHING_FEATURE?.filter(
        (label) => !['GP_GLOBAL_PLATFORM'].includes(label.id),
      ) || [],
    targetGroupLabels: (state) =>
      state.translations?.labels?.GLOBAL_PUBLISHING_CLOTHING_DEPARTMENT || [],
  },
  mutations: {
    setConfig(state, config) {
      state.config = config;
    },
    setTranslations(state, translations) {
      state.translations = translations;
    },
    setCategories(state, categories) {
      state.categories = categories;

      state.mainCategories = getMainCategories(
        categories,
        localeService.getLocale(),
      );
    },
    setAppearanceGroups(state, appearanceGroups) {
      state.appearanceGroups = appearanceGroups;
    },
    setAppearances(state, appearances) {
      state.appearances = appearances;
    },
    setShops(state, shops) {
      state.shops = shops;
    },
    setLoaded(state) {
      state.loaded = true;
      state.loadError = false;
    },
    setLoadError(state, loadError) {
      state.loadError = loadError;
    },
    setHasIntentsForScope(state, intents) {
      state.intentsForScope = intents;
    },
    setApparelTargetGroupLabels(state, labels) {
      state.apparelTargetGroupLabels = labels.map((label) => ({
        ...label,
        name: label.translations[localeService.getLocale()] || label.id,
        icon: iconForPublishingLabel[label.id] || 'questionmark',
      }));
    },
  },
  actions: {
    fetchData({ dispatch, commit }) {
      if (!dataLoadPromise) {
        dataLoadPromise = loadData({ dispatch, commit }).finally(() => {
          dataLoadPromise = null;
        });
      }

      return dataLoadPromise;
    },
    /**
     * The only safe way to wait for this data.
     *
     * `fetchData` is dispatched from the login init, but only behind
     * `settings/publishingCoreEnabled` - which reads an empty settings object,
     * and so is false, whenever `settings/fetchSettings` itself failed. It is
     * skipped for the mobile app too. A page that merely awaited a promise
     * resolved *inside* `fetchData` therefore hung forever on exactly the
     * outage this is meant to survive (DEV-231297).
     *
     * Awaiting through here instead makes the awaiter trigger the load it
     * needs, so it settles on every path. It never rejects; read `loadError`.
     */
    ensureDataLoaded({ state, dispatch }) {
      if (state.loaded) {
        return Promise.resolve();
      }

      return dispatch('fetchData');
    },
    async fetchConfig({ commit, state }) {
      if (state.config) {
        return Promise.resolve();
      }
      const data = await publishingCoreService.getConfig();
      commit('setConfig', data);
    },
    async fetchAppearanceGroups({ commit, state }) {
      if (state.appearanceGroups) {
        return Promise.resolve();
      }
      const data = await publishingCoreService.getAppearanceGroups();
      commit('setAppearanceGroups', data);
    },
    async fetchAppearances({ commit, state }) {
      if (state.appearances) {
        return Promise.resolve();
      }
      const data = await publishingCoreService.getAppearances();
      commit('setAppearances', data);
    },
    async fetchTranslations({ commit, state }) {
      // Each fetch guards on its own slice. Guarding on `state.config` meant a
      // retry after a partial failure skipped this call as soon as the config
      // had come back, leaving translations null for good (DEV-231297).
      if (state.translations) {
        return Promise.resolve();
      }
      const data = await publishingCoreService.getTranslations();
      commit('setTranslations', data);
    },
    async fetchCategories({ commit, state }) {
      if (state.categories) {
        return Promise.resolve();
      }
      const data = await publishingCoreService.getCategories();
      commit('setCategories', data);
    },
    async fetchShops({ commit, state }) {
      if (state.shops) {
        return Promise.resolve();
      }
      const data = await publishingCoreService.getShops();
      commit('setShops', data);
    },
    // Best effort, and deliberately never rejects: two of its three callers
    // (the compositions store's deleteComposition and the composition store's
    // save) fire it off unawaited to refresh the scope badges after a change,
    // so a rejection there surfaces only as an uncaught promise (DEV-231297).
    // On failure the flags keep the value they already had.
    fetchPublishingIntents: async ({ commit, dispatch }) => {
      Promise.resolve(
        dispatch('dms/fetchPartnerData', null, { root: true }),
      ).catch((error) => {
        console.error('dms/fetchPartnerData failed', error);
      });

      try {
        const counts = await Promise.all([
          publishingCoreService.getPosStateCounts({
            scope: PUBLISHING_SCOPE.MP.key,
          }),
          publishingCoreService.getPosStateCounts({
            scope: PUBLISHING_SCOPE.SHOP.key,
          }),
        ]);
        delete counts[0].stateCounts.UNPUBLISHED;
        delete counts[1].stateCounts.UNPUBLISHED;
        const intents = {
          [PUBLISHING_SCOPE.MP.key]: Object.values(counts[0].stateCounts).some(
            (count) => count > 0,
          ),
          [PUBLISHING_SCOPE.SHOP.key]: Object.values(
            counts[1].stateCounts,
          ).some((count) => count > 0),
        };
        commit('setHasIntentsForScope', intents);
      } catch (error) {
        console.error('publishingCore/fetchPublishingIntents failed', error);
      }
    },
    fetchApparelTargetGroupLabels: async ({ commit }) => {
      const labels =
        await assortmentSystemService.getLabels(APPAREL_LABEL_GROUP);
      commit('setApparelTargetGroupLabels', labels);
    },
  },
};
